import { FC } from 'react';
import { Button, Checkbox, Col, Divider, Modal, Row, Typography } from 'antd';
import { TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragDropContext, Draggable, DragHandle, Droppable, DropResult } from '@/shared/components/drag-and-drop';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

import { FinancialOverviewLayoutCustomizeModalProps, FinancialOverviewLayoutSectionKey } from '../../types';

const draggableIdFor = (key: FinancialOverviewLayoutSectionKey) => `financial-overview:${key}`;

export const FinancialOverviewLayoutCustomizeModal: FC<FinancialOverviewLayoutCustomizeModalProps> = ({
    hidden,
    onClose,
    onHiddenChange,
    onOrderChange,
    onReset,
    open,
    order,
    sections,
}) => {
    const { token } = useAntDesignTheme();
    const hiddenKeys = new Set(hidden);
    const sectionByKey = new Map(sections.map((section) => [section.key, section] as const));
    const orderedSections = order.map((key) => sectionByKey.get(key)).filter(Boolean) as typeof sections;

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        const nextOrder = [...order];
        const [moved] = nextOrder.splice(result.source.index, 1);

        nextOrder.splice(result.destination.index, 0, moved);
        onOrderChange(nextOrder);
    };

    const handleVisibilityToggle = (key: FinancialOverviewLayoutSectionKey) => {
        const section = sectionByKey.get(key);

        if (section?.locked) {
            return;
        }

        const nextHidden = new Set(hiddenKeys);

        if (nextHidden.has(key)) {
            nextHidden.delete(key);
        } else {
            nextHidden.add(key);
        }

        onHiddenChange(Array.from(nextHidden));
    };

    return (
        <Modal destroyOnClose footer={null} onCancel={onClose} open={open} title='Customize Overview' width={16 * 32}>
            <Typography.Paragraph style={{ color: token.colorTextSecondary, marginBottom: '0.75rem' }}>
                Choose which research components appear, then drag them into the order that best fits your diligence
                workflow.
            </Typography.Paragraph>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId='financial-overview-sections'>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                                backgroundColor: token.colorBgBase,
                                border: `1px dashed ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                maxHeight: 'min(60vh, 40rem)',
                                overflowY: 'auto',
                                overscrollBehavior: 'contain',
                                padding: '0.25rem',
                                scrollbarGutter: 'stable',
                            }}
                        >
                            {orderedSections.map((section, index) => (
                                <Draggable draggableId={draggableIdFor(section.key)} index={index} key={section.key}>
                                    {(provided, snapshot) => (
                                        <Row
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            align='middle'
                                            gutter={[8, 0]}
                                            style={{
                                                backgroundColor: snapshot.isDragging
                                                    ? token.colorBgElevated
                                                    : token.colorBgBase,
                                                borderRadius: token.borderRadiusSM,
                                                margin: snapshot.isDragging ? 0 : '0.25rem 0',
                                                padding: '0.375rem 0.5rem 0.375rem 0.25rem',
                                                ...provided.draggableProps.style,
                                            }}
                                            wrap={false}
                                        >
                                            <Col flex='none'>
                                                <DragHandle
                                                    dragHandleProps={provided.dragHandleProps}
                                                    isDragging={snapshot.isDragging}
                                                    label={`Reorder ${section.label}`}
                                                />
                                            </Col>
                                            <Col flex='none'>
                                                <SmallTooltip
                                                    placement='left'
                                                    title={section.locked ? 'Required' : 'Show in Overview'}
                                                >
                                                    <Checkbox
                                                        checked={!hiddenKeys.has(section.key)}
                                                        disabled={section.locked}
                                                        onChange={() => handleVisibilityToggle(section.key)}
                                                    />
                                                </SmallTooltip>
                                            </Col>
                                            <Col flex='none'>
                                                <span style={{ color: token.colorTextTertiary }}>{section.icon}</span>
                                            </Col>
                                            <Col flex='auto' style={{ minWidth: 0 }}>
                                                <div style={{ color: token.colorText }}>{section.label}</div>
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    {section.description}
                                                </small>
                                            </Col>
                                        </Row>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <Divider style={{ margin: '0.75rem 0' }} />
            <Button block onClick={onReset} type='dashed'>
                <TbRefresh />
                Reset Overview Layout
            </Button>
        </Modal>
    );
};
